/**
 * useTeamInsights — fetches paginated team list from backend.
 * Controls whether the "Team Insights" tab is shown at all.
 *
 * @param {'reportees'|'org'} scope  reportees = reporting tree (default);
 *                                   org = all org members (org_admin only)
 * @param {boolean} enabled          when false, skip fetch (e.g. non-admin org scope)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getTeam } from '../engine/backendClient';

export function useTeamInsights(scope = 'reportees', enabled = true) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearchState] = useState('');
  const pageSizeRef = useRef(12);
  const scopeRef = useRef(scope);
  const searchRef = useRef('');
  scopeRef.current = scope;
  searchRef.current = search;

  const load = useCallback(async (p, size, searchOverride) => {
    if (!enabled) {
      setTeam([]);
      setTotal(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }
    const q = searchOverride !== undefined ? searchOverride : searchRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await getTeam(p, size, scopeRef.current, q);
      setTeam(res.team || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
      setPage(p);
      setPageSize(size);
      pageSizeRef.current = size;
    } catch (err) {
      setError(err.message);
      setTeam([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setTeam([]);
      setTotal(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }
    load(1, pageSizeRef.current);
  }, [load, enabled, scope]);

  const goToPage = useCallback((p, size) => {
    const newSize = size || pageSizeRef.current;
    if (newSize !== pageSizeRef.current) {
      load(1, newSize);
    } else {
      load(p, newSize);
    }
  }, [load]);

  // ADDITIVE: search by name/email — resets to page 1
  const setSearch = useCallback((next) => {
    const value = (next || '').trimStart();
    setSearchState(value);
    searchRef.current = value;
    load(1, pageSizeRef.current, value.trim());
  }, [load]);

  return {
    team,
    loading,
    error,
    page,
    pageSize,
    total,
    totalPages,
    goToPage,
    scope,
    search,
    setSearch,
  };
}
